import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Injectable({
  providedIn: 'root',
})
export class TableExportService {
  exportTable(
    type: 'pdf' | 'csv',
    headers: string[],
    data: any[][],
    filename: string = 'export',
    columnFormats?: Record<string, any>,
    generatedDate?: string
  ): void {
    let header = headers.map((item) => this.formatColumnHeader(item));

    if (type === 'pdf') {
      this.exportToPDF(header, data, filename, generatedDate);
    } else if (type === 'csv') {
      this.exportToCSV(header, data, filename, columnFormats, generatedDate);
    }
  }

  private async exportToPDF(
    headers: string[],
    data: any[][],
    filename: string,
    generatedDate?: string,
  ): Promise<void> {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text(filename, 14, 15);
    
    // Add generated date if provided
    if (generatedDate) {
      doc.setFontSize(10);
      doc.text(generatedDate, 14, 22);
    }

    autoTable(doc, {
      head: [headers],
      body: data,
      startY: generatedDate ? 32 : 25,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'wrap',
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 'auto', minCellWidth: 25 },
      },
    });

    // Get PDF as blob
    const pdfBlob = doc.output('blob');

    // Use File System Access API to show save dialog
    await this.saveFileWithDialog(
      pdfBlob,
      `${filename}.pdf`,
      'application/pdf',
    );
  }

  private async exportToCSV(
    headers: string[],
    data: any[][],
    filename: string,
    columnFormats?: Record<string, any>,
    generatedDate?: string,
  ): Promise<void> {
    if (columnFormats) {
      Object.keys(columnFormats).forEach((columnName) => {
        const columnIndex = headers.indexOf(columnName);
        if (columnIndex !== -1) {
          if (
            columnFormats[columnName]?.format === 'text' ||
            columnName.toLowerCase().includes('phone')
          ) {
            data = data.map((row) => {
              const newRow = [...row];
              const cellValue = newRow[columnIndex];
              if (cellValue && cellValue !== '-') {
                newRow[columnIndex] =
                  `"\t${String(cellValue).replace(/"/g, '""')}"`;
              }
              return newRow;
            });
          }
        }
      });
    }

    let csvContent = '';
    
    // Add generated date comment at the top of CSV if provided
    if (generatedDate) {
      csvContent += `# ${generatedDate}\n`;
    }
    
    csvContent += headers.map((h) => this.escapeCSVValue(h)).join(',') + '\n';
    data.forEach((row) => {
      csvContent +=
        row
          .map((cell, index) => {
            if (
              typeof cell === 'string' &&
              cell.startsWith('"\t') &&
              cell.endsWith('"')
            ) {
              return cell;
            }

            const columnName = headers[index];
            if (
              columnName &&
              columnName.toLowerCase().includes('phone') &&
              cell &&
              cell !== '-' &&
              !cell.toString().startsWith('\t')
            ) {
              return `"\t${String(cell).replace(/"/g, '""')}"`;
            }
            if (
              typeof cell === 'string' &&
              cell.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)
            ) {
              return this.escapeCSVValue('\t' + cell);
            }

            return this.escapeCSVValue(cell);
          })
          .join(',') + '\n';
    });

    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    await this.saveFileWithDialog(csvBlob, `${filename}.csv`, 'text/csv');
  }

  private async saveFileWithDialog(
    blob: Blob,
    defaultFilename: string,
    mimeType: string,
  ): Promise<void> {
    if ('showSaveFilePicker' in window) {
      try {
        const options = {
          suggestedName: defaultFilename,
          types: [
            {
              description:
                mimeType === 'application/pdf' ? 'PDF Files' : 'CSV Files',
              accept: {
                [mimeType]: [mimeType === 'application/pdf' ? '.pdf' : '.csv'],
              },
            },
          ],
        };

        const fileHandle = await (window as any).showSaveFilePicker(options);
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error saving file:', error);
          this.fallbackDownload(blob, defaultFilename);
        }
      }
    } else {
      this.fallbackDownload(blob, defaultFilename);
    }
  }

  private fallbackDownload(blob: Blob, filename: string): void {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  }

  private escapeCSVValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const stringValue = String(value);
    if (stringValue.startsWith('"') && stringValue.endsWith('"')) {
      return stringValue;
    }
    if (
      stringValue.includes(',') ||
      stringValue.includes('"') ||
      stringValue.includes('\n') ||
      stringValue.includes('\r')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  }

  formatColumnHeader(column: string): string {
    if (column === 'PD points') {
      return 'PD points';
    }
    return (
      column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, '$1')
    );
  }
}